import React from "react";
import PropTypes from "prop-types";
import Sparkline from "./Sparkline.jsx";
import KpiCard from "../../../components/ui-components/KpiCard.jsx";

// Money formatter — euro, no decimals, locale-aware thousands separators. Finance scans the
// right edge of every number, so all KPI numbers use the same shape.
const euro = (n) => `€${Math.round(n || 0).toLocaleString("en-US")}`;
// Percent — clamps to whole numbers so a card can't read "27.4%" next to "27%" and look
// inconsistent. The underlying analytics has fractional precision; the headline metric does not.
const pct = (n) => `${Math.round((n || 0) * 100)}%`;

// Headline KPI strip for /allocation. Replaces the previous bright-green banner. Built to
// answer the three questions finance opens this page with:
//   1. How much is at stake?      → Total cost
//   2. Where's the problem?       → Unallocated (€ + % of total)
//   3. Are the rules I have good? → High-confidence share + rule coverage
//
// Counts and €'s come straight from the matrix endpoint — no new data dependency. The
// strip stays informative even before any rules exist (the empty-state case is exactly
// when finance needs to see "€42k is at risk", not a green check).
const OwnershipKpiStrip = ({
  sources = [],
  dimensions = [],
  highConfPct = 0,
  noOwnerCost = 0,
  notCoveredCost = 0,
  primaryDimension = null,
  unallocatedTrend = [],
}) => {
  // coveredCost is what the sources actually claim — the sum of the matrix rows. The REAL
  // cost base is larger: it also includes cost that no source matches at all (notCoveredCost,
  // from the backend's uncoveredCost). The matrix can only ever show coveredCost, so the
  // headline Total must add the uncovered slice back or it understates real spend.
  const coveredCost = sources.reduce((acc, s) => acc + (s.monthlyCost || 0), 0);
  const realTotalCost = coveredCost + notCoveredCost;

  // Unallocated = cost with no owner on the primary (organisational) dimension + cost that
  // matches no source at all. The two buckets never overlap: "no owner" is cost INSIDE a
  // source that lacks a rule; "not covered" is cost OUTSIDE every source. The denominator is
  // the real cost base (covered + uncovered), so the percentage stays honest even when some
  // cost escapes all sources (notCoveredCost > 0). When it's 0, this equals coveredCost.
  const unallocatedCost = noOwnerCost + notCoveredCost;
  const totalDenom = realTotalCost > 0 ? realTotalCost : 1; // avoid /0 — empty account shows 0%
  const unallocPct = unallocatedCost / totalDenom;

  // Rule coverage = filled cells / possible cells. "Possible cells" is sources × dimensions
  // because every source could have a rule on every dimension. Provides a single number
  // that climbs as the user fills in the matrix — a progress signal that the green bar
  // couldn't give.
  let filledCells = 0;
  let possibleCells = sources.length * dimensions.length;
  sources.forEach((s) => {
    dimensions.forEach((d) => {
      if (s.cells?.[d.id]) filledCells += 1;
    });
  });
  const coverageRatio = possibleCells > 0 ? filledCells / possibleCells : 0;

  // Sources that need attention on the primary (organisational) dimension: no owner rule
  // there. Scoping to the primary dimension keeps the whole Unallocated card telling one
  // story — the same dimension the € figure measures — rather than mixing in the other
  // three dimensions. Falls back to the all-dimension "any cell missing/not-high" definition
  // when no primary dimension is supplied.
  const needsAttentionCount = primaryDimension
    ? sources.filter((s) => !s.cells?.[primaryDimension.id]).length
    : sources.filter((s) =>
        !dimensions.every((d) => {
          const cell = s.cells?.[d.id];
          return cell && cell.confidenceLabel === "high";
        })
      ).length;

  // Tone rules — amber when unallocated is meaningful, ok-green only when truly low (<5%).
  // The previous green bar lit up at any allocation > 0%, which is exactly the wrong
  // signal for a finance audience.
  const unallocTone =
    unallocPct >= 0.5 ? "attention" : unallocPct >= 0.1 ? "attention" : "ok";
  const confTone = highConfPct >= 0.8 ? "ok" : highConfPct >= 0.4 ? "neutral" : "attention";
  const coverageTone = coverageRatio >= 0.8 ? "ok" : coverageRatio >= 0.25 ? "neutral" : "attention";

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <KpiCard
        label="Total cost"
        value={euro(realTotalCost)}
        sub={
          notCoveredCost > 0 ? (
            <span>
              <span className="text-text-2">{euro(coveredCost)} covered</span>
              {" + "}
              <span className="text-warning">{euro(notCoveredCost)} not covered by a source</span>
            </span>
          ) : (
            `${sources.length} source${sources.length === 1 ? "" : "s"}`
          )
        }
        info="Real cost base over the last 30 days: cost matched to a source (covered) plus cost no source matches at all (not covered)."
      />
      <KpiCard
        label={primaryDimension ? `Unallocated · ${primaryDimension.name}` : "Unallocated"}
        value={euro(unallocatedCost)}
        sub={
          <span>
            <span className="tabular-nums">{pct(unallocPct)}</span> of total ·{" "}
            <span className="text-text-2">{euro(noOwnerCost)} no owner</span>
            {" + "}
            <span className="text-text-2">{euro(notCoveredCost)} not covered by a source</span>
            {needsAttentionCount > 0 && (
              <> · <span className="text-warning">{needsAttentionCount} source{needsAttentionCount === 1 ? "" : "s"} need attention</span></>
            )}
          </span>
        }
        tone={unallocTone}
        info={
          "Cost with no organisational owner. Two parts: cost a source matched but no owner rule claims (no owner), " +
          "plus cost that matches no source at all (not covered by a source). Lower is better."
        }
        chart={
          unallocatedTrend.length > 0 ? (
            <div title="Trend over the last 30 days">
              <Sparkline data={unallocatedTrend} goodDirection="down" ariaLabel="Unallocated trend" fill width={200} height={40} />
            </div>
          ) : null
        }
      />
      <KpiCard
        label="Strong rules"
        value={pct(highConfPct)}
        sub="Share of cost matched by high-strength rules · target ≥ 80%"
        tone={confTone}
        info="Percent of allocated cost matched by high-strength rules (direct rules score highest, proportional and fixed lower)."
      />
      <KpiCard
        label="Rule coverage"
        value={`${filledCells} / ${possibleCells}`}
        sub={
          <span>
            <span className="tabular-nums">{pct(coverageRatio)}</span> complete · {sources.length} source{sources.length === 1 ? "" : "s"} × {dimensions.length} dim{dimensions.length === 1 ? "" : "s"}
          </span>
        }
        tone={coverageTone}
        info="Configuration progress — number of source/dimension cells that have a rule, out of the total possible."
      />
    </div>
  );
};

OwnershipKpiStrip.propTypes = {
  // Matrix-shape sources (as returned by /matrix → sources).
  sources: PropTypes.array,
  // The dimension definitions ([{id, name}, ...]).
  dimensions: PropTypes.array,
  // Aggregated headline share from the backend's coverage rows. Independently of the
  // matrix — the backend knows the cost-row-level allocation outcome (which the matrix
  // configuration view cannot derive client-side).
  highConfPct: PropTypes.number,
  // Cost matched by a source but with no owner rule on the primary dimension. Derived from
  // the live matrix so it always reconciles with the grid below.
  noOwnerCost: PropTypes.number,
  // Cost that matches no source at all (backend uncoveredCost). Invisible to the matrix, so
  // it must be supplied separately; together with noOwnerCost it forms the Unallocated total.
  notCoveredCost: PropTypes.number,
  // The dimension the Unallocated figure is measured on ({id, name}); labels the card and
  // scopes the "needs attention" count. Omit to fall back to the all-dimension definition.
  primaryDimension: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  // Per-day series of unallocated cost for the sparkline. Aggregated client-side from
  // the coverage rows so the chart doesn't need a separate backend endpoint.
  unallocatedTrend: PropTypes.array,
};

export default OwnershipKpiStrip;
